/**
 * SDFGenerator - Generate Signed Distance Fields from meshes
 * 
 * Uses BVH for fast closest-point queries and ray casting for inside/outside.
 */

import {
  BufferGeometry,
  DoubleSide,
  Float32BufferAttribute,
  Mesh,
  MeshBasicMaterial,
  Ray,
  Raycaster,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import { MeshBVH, acceleratedRaycast } from 'three-mesh-bvh';
import { VoxelGrid } from './VoxelGrid';
import type { VoxelGridConfig } from './types';
import type { SdfWorkerPool, SerializedSignedPart } from './sdfWorkerPool';
import {
  buildPseudoNormals,
  createUnionQueryScratch,
  unionSignedDistance,
  type SignedPart,
} from './unionSignedDistance';

export type SDFSignMode = 'boundary-flood' | 'insertion-columns' | 'multi-axis-raycast';

export type SDFGenerateConfig = VoxelGridConfig & {
  /** Use vertical column rays (geometry should be aligned so insertion ≈ +Z). */
  signMode?: SDFSignMode;
  /** Emit source-stage diagnostics to explain suspicious outside voxels. */
  diagnostics?: boolean;
  /**
   * Narrow-band mode (multi-axis-raycast only): compute exact closest-point distance ONLY
   * within this many mm of the surface; clamp everything else to a ±band sentinel (correct
   * sign, no expensive query). Skips the closest-point query for the bulk of empty/far-interior
   * voxels. Only safe when downstream reads exact distance solely inside this band.
   */
  narrowBandMm?: number;
  /**
   * Boundary-flood mode only: treat every part as a SLAB of this thickness
   * behind its surface (sd_i → max(sd_i, −sd_i − slab)) before the union min.
   * The returned field is then directly the hollow-shell SDF — no separate
   * shell transform needed — and is the correct semantics for OPEN sheet
   * parts (trimmed scan bands, crown shells), whose unbounded "behind"
   * distances would otherwise poison the union. Skips the flood fill
   * entirely: with slabs, everything farther than the slab is air.
   */
  sheetSlabMm?: number;
  /**
   * Boundary-flood mode with slab OFF: declare the input an OPEN sheet (a
   * trimmed scan band, a capped-but-leaky jaw). An open sheet always "drains"
   * the flood, so the seal escalation can never succeed — skip it (and the
   * drained-volume warning) and classify the far field with a single lightly
   * sealed flood (≈0.3 mm closure, enough to plug leftover seam slits).
   * Enclosed pockets — fused interproximal necks, crown interiors — still
   * classify as inside, which is what solid-level CSG post-passes (the
   * interproximal carve) need from the far field before the slab transform.
   */
  openSheet?: boolean;
};

type AxisIndex = 0 | 1 | 2;

let bvhPatched = false;
const WORST_RAY_LIMIT = 10;

// Neighborhood offsets for the narrow-band mask (6-conn for boundary detection, 26-conn for
// the Chebyshev dilation that covers every voxel within the band radius).
const NEIGHBOR_OFFSETS_6: ReadonlyArray<readonly [number, number, number]> = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];
const NEIGHBOR_OFFSETS_26: ReadonlyArray<readonly [number, number, number]> = (() => {
  const offsets: Array<readonly [number, number, number]> = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx !== 0 || dy !== 0 || dz !== 0) offsets.push([dx, dy, dz]);
      }
    }
  }
  return offsets;
})();

function ensureBVHPatched(): void {
  if (!bvhPatched) {
    (Mesh as any).prototype.raycast = acceleratedRaycast;
    bvhPatched = true;
  }
}

export class SDFGenerator {
  private geometry: BufferGeometry;
  private bvh: MeshBVH;
  /** Corner ranges of each input mesh inside the merged geometry. */
  private partRanges: Array<{ start: number; count: number }> = [];
  private signedParts: SignedPart[] | null = null;

  constructor(meshes: Mesh[]) {
    ensureBVHPatched();
    this.geometry = this.mergeGeometries(meshes);
    this.bvh = (this.geometry as any).boundsTree;
  }

  /**
   * Generate SDF on a voxel grid
   */
  generate(config: SDFGenerateConfig): VoxelGrid {
    this.geometry.computeBoundingBox();
    const grid = VoxelGrid.fromBounds(this.geometry.boundingBox!, config);

    if (config.signMode === 'multi-axis-raycast' && config.narrowBandMm && config.narrowBandMm > 0) {
      this.applyMultiAxisNarrowBand(grid, config.narrowBandMm, config.diagnostics ?? false);
      return grid;
    }

    this.computeUnsignedDistances(grid);

    if (config.signMode === 'insertion-columns') {
      this.applyInsertionColumnSigns(grid);
    } else if (config.signMode === 'multi-axis-raycast') {
      this.applyMultiAxisRaycastSigns(grid, config.diagnostics ?? false);
    } else {
      this.applyInsideOutsideSigns(grid);
    }

    return grid;
  }

  /**
   * Sample unsigned closest-point distance to this generator's geometry onto a
   * fresh grid that shares `reference`'s lattice (origin / cellSize / counts).
   *
   * Lets a caller measure distance to a *different* mesh on an already-built
   * grid so the two fields are voxel-aligned and can be combined element-wise
   * (e.g. distance to the uncapped teeth on the capped SDF's grid).
   */
  sampleUnsignedDistanceOn(reference: VoxelGrid): VoxelGrid {
    const grid = new VoxelGrid(reference.info);
    this.computeUnsignedDistances(grid);
    return grid;
  }

  /**
   * Compute closest point distance for each voxel
   */
  private computeUnsignedDistances(grid: VoxelGrid): void {
    const { counts, origin, cellSize } = grid.info;
    const point = new Vector3();
    const closestPoint = new Vector3();
    const hitInfo = { point: closestPoint, distance: 0, faceIndex: 0 };

    let idx = 0;
    for (let x = 0; x < counts.x; x++) {
      const wx = origin.x + x * cellSize;
      for (let y = 0; y < counts.y; y++) {
        const wy = origin.y + y * cellSize;
        for (let z = 0; z < counts.z; z++) {
          const wz = origin.z + z * cellSize;
          point.set(wx, wy, wz);
          this.bvh.closestPointToPoint(point, hitInfo as any);
          grid.data[idx] = hitInfo.distance;
          idx++;
        }
      }
    }
  }

  /**
   * Determine inside/outside using ray casting and apply signs
   */
  private applyInsideOutsideSigns(grid: VoxelGrid): void {
    const { counts, strides, cellSize } = grid.info;
    const total = grid.info.total;
    const visited = new Uint8Array(total); // 1 = outside
    const queue: number[] = [];
    const surfaceEps = cellSize * 0.6; // allow traversal only through clear air

    const push = (idx: number) => {
      if (!visited[idx]) {
        visited[idx] = 1;
        queue.push(idx);
      }
    };

    // Seed all boundary voxels as outside
    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        push(x * strides.x + y * strides.y + 0);
        push(x * strides.x + y * strides.y + (counts.z - 1));
      }
    }
    for (let x = 0; x < counts.x; x++) {
      for (let z = 0; z < counts.z; z++) {
        push(x * strides.x + 0 * strides.y + z);
        push(x * strides.x + (counts.y - 1) * strides.y + z);
      }
    }
    for (let y = 0; y < counts.y; y++) {
      for (let z = 0; z < counts.z; z++) {
        push(0 * strides.x + y * strides.y + z);
        push((counts.x - 1) * strides.x + y * strides.y + z);
      }
    }

    // 6-connected flood through voxels that are clearly outside
    const neighbors = [
      [1, 0, 0],
      [-1, 0, 0],
      [0, 1, 0],
      [0, -1, 0],
      [0, 0, 1],
      [0, 0, -1],
    ];

    while (queue.length) {
      const idx = queue.pop()!;
      const { x, y, z } = grid.coords(idx);

      for (const [dx, dy, dz] of neighbors) {
        const nx = x + dx;
        const ny = y + dy;
        const nz = z + dz;
        if (!grid.inBounds(nx, ny, nz)) continue;

        const nidx = grid.index(nx, ny, nz);
        if (visited[nidx]) continue;

        // Only travel through space that is not near the surface
        if (grid.data[nidx] > surfaceEps) {
          push(nidx);
        }
      }
    }

    // Apply sign: anything not reached from outside becomes inside (negative)
    for (let i = 0; i < total; i++) {
      if (!visited[i]) {
        grid.data[i] = -grid.data[i];
      }
    }
  }

  /**
   * Sign via three orthogonal-axis ray sweeps with majority vote. Each (y,z) row
   * along +X (resp. (x,z) along +Y, (x,y) along +Z) collects all surface hits
   * once via `bvh.raycast`, dedupes hits within `cellSize * 0.25` of each other
   * (kills coincident-triangle artefacts at the tooth-cap seam), and toggles an
   * inside flag at each voxel cross-over. A voxel is "inside" if at least 2 of
   * the 3 axes report it inside.
   *
   * For 1-of-3 voxels within `cellSize` of the surface, we tiebreak using the
   * closest-point face normal (poor-man's winding number): if the face normal
   * points away from the voxel, the voxel sits on the inside side of the face.
   */
  private applyMultiAxisRaycastSigns(grid: VoxelGrid, diagnosticsEnabled: boolean): void {
    const total = grid.info.total;
    const insideCount = new Uint8Array(total);

    const axisReports = [
      this.sweepAxis(grid, 0, insideCount),
      this.sweepAxis(grid, 1, insideCount),
      this.sweepAxis(grid, 2, insideCount),
    ];

    const ambiguousIndices: number[] = [];
    const voteHistogram = new Uint32Array(4);
    let farInsideCount = 0;
    const farThreshold = grid.info.cellSize * 4;
    for (let i = 0; i < total; i++) {
      const vote = insideCount[i];
      voteHistogram[vote]++;
      if (insideCount[i] >= 2) {
        grid.data[i] = -grid.data[i];
        if (grid.data[i] < -farThreshold) farInsideCount++;
      } else if (insideCount[i] === 1 && grid.data[i] < grid.info.cellSize) {
        ambiguousIndices.push(i);
      }
    }

    if (ambiguousIndices.length === 0) return;

    this.resolveAmbiguousByNormal(grid, ambiguousIndices);

    if (diagnosticsEnabled) {
      const oddRayRateByAxis = axisReports.map((report) => ({
        axis: report.axis,
        oddParityRays: report.oddParityRays,
        totalRays: report.totalRays,
        oddPct: report.totalRays > 0 ? Number(((100 * report.oddParityRays) / report.totalRays).toFixed(2)) : 0,
        avgTrailingInsideCellsOnOdd: report.oddParityRays > 0
          ? Number((report.trailingInsideCellsOnOdd / report.oddParityRays).toFixed(2))
          : 0,
      }));
      const worstOddRaysByAxis = axisReports.map((report) => ({
        axis: report.axis,
        rays: report.worstOddRays,
      }));

      // If many far voxels require 2/3 votes, that typically means parity rays leaked.
      const suspicious = farInsideCount > total * 0.005 || oddRayRateByAxis.some((r) => r.oddPct > 5);
      const logger = suspicious ? console.warn : console.info;
      logger('[SDFGenerator][multi-axis-diagnostics]', {
        grid: `${grid.info.counts.x}x${grid.info.counts.y}x${grid.info.counts.z}`,
        cellSize: grid.info.cellSize,
        voteHistogram: {
          v0: voteHistogram[0],
          v1: voteHistogram[1],
          v2: voteHistogram[2],
          v3: voteHistogram[3],
        },
        farInsideThresholdMm: Number(farThreshold.toFixed(3)),
        farInsideCount,
        ambiguousNearSurfaceCount: ambiguousIndices.length,
        oddRayRateByAxis,
        worstOddRaysByAxis,
      });
    }
  }

  /**
   * Narrow-band multi-axis sign + distance (synchronous). Computes exact closest-point
   * distance ONLY for voxels within `maxBandMm` of the surface; everything else gets a
   * ±maxBandMm sentinel (correct sign, clamped magnitude — never read beyond its sign
   * downstream). See `buildNearSurfaceMask` for the mask-coverage proof.
   */
  private applyMultiAxisNarrowBand(grid: VoxelGrid, maxBandMm: number, diagnosticsEnabled: boolean): void {
    const { insideCount, axisReports } = this.computeInsideCounts(grid);
    const { mask, maskedIndices, bandCells } = this.buildNearSurfaceMask(grid, insideCount, maxBandMm);
    this.assembleNarrowBandSync(grid, insideCount, mask, maskedIndices, maxBandMm);
    if (diagnosticsEnabled) {
      this.logNarrowBandDiagnostics(grid, insideCount, axisReports, maxBandMm, bandCells, maskedIndices.length);
    }
  }

  /**
   * Narrow-band generation with the masked closest-point pass fanned out to a worker pool.
   * Falls back to the synchronous path when the pool is unavailable, the mode isn't
   * `multi-axis-raycast`, no `narrowBandMm` was given, or a worker errors mid-run.
   */
  async generateAsync(config: SDFGenerateConfig, pool: SdfWorkerPool | null): Promise<VoxelGrid> {
    const canParallelize =
      pool && config.signMode === 'multi-axis-raycast' && config.narrowBandMm && config.narrowBandMm > 0;
    if (!canParallelize) {
      return this.generate(config);
    }

    this.geometry.computeBoundingBox();
    const grid = VoxelGrid.fromBounds(this.geometry.boundingBox!, config);
    const maxBandMm = config.narrowBandMm!;

    const t0 = performance.now();
    const { insideCount, axisReports } = this.computeInsideCounts(grid);
    const t1 = performance.now();
    const { mask, maskedIndices, bandCells } = this.buildNearSurfaceMask(grid, insideCount, maxBandMm);
    const t2 = performance.now();
    let workerMs = 0;

    try {
      const position = this.geometry.getAttribute('position').array as Float32Array;
      const serializedBvh = MeshBVH.serialize(this.bvh);
      const { origin, cellSize, counts } = grid.info;
      const distances = await pool!.computeDistances(
        {
          position,
          bvh: serializedBvh,
          grid: { ox: origin.x, oy: origin.y, oz: origin.z, cellSize, cx: counts.x, cy: counts.y, cz: counts.z },
        },
        maskedIndices,
      );
      workerMs = performance.now() - t2;
      this.assembleNarrowBandFromDistances(grid, insideCount, mask, maskedIndices, maxBandMm, distances);
    } catch (error) {
      console.warn('[SDFGenerator] worker distance pass failed; falling back to sync.', error);
      this.assembleNarrowBandSync(grid, insideCount, mask, maskedIndices, maxBandMm);
    }

    if (config.diagnostics) {
      const t3 = performance.now();
      this.logNarrowBandDiagnostics(grid, insideCount, axisReports, maxBandMm, bandCells, maskedIndices.length);
      // Serial (sweeps + mask + assembly) vs parallel (worker distances) split: shows how much
      // of source-sdf even *can* shrink with more workers (the rest is Amdahl-bound).
      console.info('[SDFGenerator][parallel-timing]', {
        workers: pool!.workerCount,
        sweepsMs: Number((t1 - t0).toFixed(1)),
        maskMs: Number((t2 - t1).toFixed(1)),
        workerDistancesMs: Number(workerMs.toFixed(1)),
        assembleMs: Number((t3 - t2 - workerMs).toFixed(1)),
      });
    }
    return grid;
  }

  /**
   * Narrow-band SDF whose SIGN comes from a boundary flood fill instead of ray
   * parity: the triangles are rasterized into a boundary-voxel mask, the
   * exterior is flood-filled from the grid faces, and every unreached voxel
   * counts as inside. No parity counting means overlapping closed parts
   * (e.g. tooth roots intersecting the gum body) cannot flip the sign, and the
   * flood self-seals (see floodOutsideSealed) so seam slits and rasterizer
   * pinholes cannot drain the interior either. Exact closest-point distances
   * are computed only within `narrowBandMm` of the boundary (worker pool when
   * available); everything else is clamped to ±narrowBandMm.
   *
   * Pre-subdividing oversized triangles still helps: the sealing radius is
   * chosen from the data, and fewer pinholes means a tighter seal.
   */
  async generateNarrowBandFlood(
    config: SDFGenerateConfig,
    pool: SdfWorkerPool | null,
  ): Promise<VoxelGrid> {
    this.geometry.computeBoundingBox();
    const grid = VoxelGrid.fromBounds(this.geometry.boundingBox!, config);
    const cell = grid.info.cellSize;
    const band = Math.max(config.narrowBandMm ?? cell * 6, cell * 3);
    const slab = config.sheetSlabMm ?? 0;

    const boundary = this.rasterizeBoundaryMask(grid);
    const maskedIndices = collectBandIndices(grid, boundary, Math.ceil(band / cell) + 1);

    // Slab mode needs no inside/outside classification at all: everything
    // farther than the slab behind a sheet is air, so the flood (and its
    // sealing escalation) is skipped and the far field is uniformly positive.
    let outside: Uint8Array | null = null;
    if (slab <= 0 && config.openSheet) {
      const sealRadius = Math.min(MAX_SEAL_RADIUS, Math.max(1, Math.round(0.3 / cell)));
      outside = floodOutsideMask(grid, closeMask(grid, boundary, sealRadius));
      if (config.diagnostics) {
        const enclosed = (grid.info.total - countMask(outside)) * cell ** 3;
        console.log(
          `[SDFGenerator] open-sheet flood: cell ${cell.toFixed(3)} mm, seal radius ${sealRadius}, ` +
            `enclosed pockets ${enclosed.toFixed(0)} mm³, band ${maskedIndices.length} voxels`,
        );
      }
    } else if (slab <= 0) {
      const referenceVolume = this.meshVolume();
      const flood = floodOutsideSealed(grid, boundary, referenceVolume);
      outside = flood.outside;
      const enclosed = flood.insideVoxels * cell ** 3;
      if (enclosed < referenceVolume * DRAIN_THRESHOLD) {
        // Not fatal: band values are geometric (union signed distance), so open
        // input still yields a clean slab-shell around the sheet. The flood only
        // provides far-field sentinels and enclosed-void semantics.
        console.warn(
          `[SDFGenerator] input is not a closed volume (enclosed ${enclosed.toFixed(0)} mm³ of ` +
            `${referenceVolume.toFixed(0)} mm³ reference at seal radius ${flood.sealRadius}). ` +
            'This is expected for open sheets (e.g. a trimmed gum band); consider sheetSlabMm.',
        );
      }
      if (config.diagnostics) {
        console.log(
          `[SDFGenerator] flood: cell ${cell.toFixed(3)} mm, seal radius ${flood.sealRadius}, ` +
            `enclosed ${enclosed.toFixed(0)} mm³ of ${referenceVolume.toFixed(0)} mm³ mesh, ` +
            `band ${maskedIndices.length} voxels, per-radius [${flood.volumeCurve.join(', ')}]`,
        );
      }
    } else if (config.diagnostics) {
      console.log(
        `[SDFGenerator] slab union: cell ${cell.toFixed(3)} mm, slab ${slab.toFixed(2)} mm, ` +
          `band ${maskedIndices.length} voxels`,
      );
    }

    // Every band cell gets the UNION signed distance (min over parts of the
    // signed distance to that part) — magnitude AND sign from the geometry
    // itself. One field semantics everywhere is essential: mixing per-part
    // signs with merged-mesh magnitudes leaves one-cell value jumps along
    // internal (overlapped) walls that the shell transform turns into empty
    // sheets inside solid material.
    let values: Float32Array | null = null;
    let flags: Uint8Array | null = null;
    if (pool) {
      try {
        const { origin, cellSize, counts } = grid.info;
        const result = await pool.computeUnionSignedDistances(
          this.serializeSignedParts(),
          { ox: origin.x, oy: origin.y, oz: origin.z, cellSize, cx: counts.x, cy: counts.y, cz: counts.z },
          maskedIndices,
          slab,
        );
        values = result.values;
        flags = result.flags;
      } catch (error) {
        console.warn('[SDFGenerator] worker distance pass failed; falling back to sync.', error);
      }
    }
    if (!values || !flags) {
      values = new Float32Array(maskedIndices.length);
      flags = new Uint8Array(maskedIndices.length);
      const parts = this.getSignedParts();
      const scratch = createUnionQueryScratch();
      const { origin, cellSize, strides } = grid.info;
      for (let k = 0; k < maskedIndices.length; k++) {
        const idx = maskedIndices[k];
        const x = Math.floor(idx / strides.x);
        const rem = idx - x * strides.x;
        const y = Math.floor(rem / strides.y);
        const z = rem - y * strides.y;
        scratch.point.set(origin.x + x * cellSize, origin.y + y * cellSize, origin.z + z * cellSize);
        const result = unionSignedDistance(parts, scratch, slab);
        values[k] = result.sd;
        flags[k] = result.ambiguous ? 1 : 0;
      }
    }

    // Assemble: far field from the sealed flood (uniform air in slab mode),
    // band from the union signed distances. Ambiguous cells (near-tangential
    // closest feature) fall back to the flood sign for now and are settled by
    // the Lipschitz pass; cells signed "outside" but unreachable from the
    // exterior (enclosed voids) are flipped inside so the band agrees with the
    // far field — meaningless for slabs, where positive far field is exact.
    const data = grid.data;
    if (outside) {
      for (let i = 0; i < data.length; i++) data[i] = outside[i] ? band : -band;
    } else {
      data.fill(band);
    }
    const total = grid.info.total;
    const inBand = new Uint8Array(total);
    const ambiguous = new Uint8Array(total);
    const magnitudes = new Float32Array(maskedIndices.length);
    for (let k = 0; k < maskedIndices.length; k++) {
      const idx = maskedIndices[k];
      inBand[idx] = 1;
      const sd = values[k];
      if (!Number.isFinite(sd)) {
        magnitudes[k] = band;
        continue; // nothing found (cannot happen in-band, but keep the far-field value)
      }
      const magnitude = Math.min(Math.abs(sd), band);
      magnitudes[k] = magnitude;
      if (flags[k]) {
        ambiguous[idx] = 1;
        data[idx] = outside && !outside[idx] ? -magnitude : magnitude;
      } else if (sd === 0) {
        data[idx] = -1e-5; // cell centre on the surface: iso passes through it
      } else {
        data[idx] = Math.sign(sd) * magnitude;
      }
    }
    if (outside) reconnectOutsidePositive(grid, inBand, outside, maskedIndices);
    resolveBoundarySignsByLipschitz(grid, ambiguous, maskedIndices, magnitudes, band);
    if (config.diagnostics) {
      let flagged = 0;
      for (let k = 0; k < maskedIndices.length; k++) if (flags[k]) flagged++;
      console.log(
        `[SDFGenerator] union signing: ${flagged} of ${maskedIndices.length} band cells ambiguous ` +
          `(${((flagged / Math.max(1, maskedIndices.length)) * 100).toFixed(2)} %)`,
      );
    }
    return grid;
  }

  /**
   * Per-part signed-distance query structures, lazily built. A single-part
   * input reuses the merged geometry and BVH; a multi-part input slices the
   * merged positions per part (biggest part — the gum — first, so the bbox
   * pruning in unionSignedDistance settles `best` early and skips most tooth
   * queries). Pseudo-normals are built AFTER the BVH so face order matches the
   * faceIndex space the BVH reports.
   */
  private getSignedParts(): SignedPart[] {
    if (this.signedParts) return this.signedParts;
    const makePart = (geometry: BufferGeometry, bvh: MeshBVH): SignedPart => {
      geometry.computeBoundingBox();
      const positions = geometry.getAttribute('position').array as Float32Array;
      const index = geometry.getIndex();
      return {
        bvh,
        positions,
        index: index ? (index.array as ArrayLike<number>) : null,
        pn: buildPseudoNormals(positions, index ? (index.array as ArrayLike<number>) : null),
        boxMin: geometry.boundingBox!.min,
        boxMax: geometry.boundingBox!.max,
      };
    };
    if (this.partRanges.length <= 1) {
      this.signedParts = [makePart(this.geometry, this.bvh)];
      return this.signedParts;
    }
    const merged = this.geometry.getAttribute('position').array as Float32Array;
    this.signedParts = this.partRanges
      .map((range) => {
        const geometry = new BufferGeometry();
        geometry.setAttribute(
          'position',
          new Float32BufferAttribute(
            merged.subarray(range.start * 3, (range.start + range.count) * 3),
            3,
          ),
        );
        return makePart(geometry, new MeshBVH(geometry));
      })
      .sort((p, q) => q.positions.length - p.positions.length);
    return this.signedParts;
  }

  /** Transferable snapshot of the signed parts for the worker pool. */
  private serializeSignedParts(): SerializedSignedPart[] {
    return this.getSignedParts().map((part) => ({
      // slice(): part positions are subarray views into the merged buffer, and
      // structured clone would ship the WHOLE underlying buffer per part.
      position: part.positions.slice(),
      bvh: MeshBVH.serialize(part.bvh),
      pn: part.pn,
      boxMin: { x: part.boxMin.x, y: part.boxMin.y, z: part.boxMin.z },
      boxMax: { x: part.boxMax.x, y: part.boxMax.y, z: part.boxMax.z },
    }));
  }

  /**
   * Enclosed volume by the divergence theorem, about the bounding-box centre.
   *
   * This is the yardstick the flood is checked against: it needs no watertight
   * input (open parts only contribute the small cone over their border, which
   * the centred origin keeps minimal) and it over- rather than under-counts a
   * union of overlapping parts, so falling far short of it is unambiguous
   * evidence that the exterior drained the model rather than a modelling
   * quirk. Overlaps are double-counted, hence "reference", not "truth".
   */
  private meshVolume(): number {
    const position = this.geometry.getAttribute('position');
    const index = this.geometry.getIndex();
    this.geometry.computeBoundingBox();
    const centre = this.geometry.boundingBox!.getCenter(new Vector3());
    const a = new Vector3();
    const b = new Vector3();
    const c = new Vector3();
    const cross = new Vector3();
    const count = index ? index.count : position.count;
    let volume = 0;
    for (let i = 0; i < count; i += 3) {
      const i0 = index ? index.getX(i) : i;
      const i1 = index ? index.getX(i + 1) : i + 1;
      const i2 = index ? index.getX(i + 2) : i + 2;
      a.fromBufferAttribute(position, i0).sub(centre);
      b.fromBufferAttribute(position, i1).sub(centre);
      c.fromBufferAttribute(position, i2).sub(centre);
      volume += cross.crossVectors(b, c).dot(a) / 6;
    }
    return Math.abs(volume);
  }

  /**
   * Conservative boundary rasterization: every triangle is barycentrically
   * sampled at ~0.45×cell spacing and each sample stamps its voxel. Assumes
   * the caller pre-subdivided oversized triangles (see generateNarrowBandFlood).
   */
  private rasterizeBoundaryMask(grid: VoxelGrid): Uint8Array {
    const { origin, cellSize, counts, strides, total } = grid.info;
    const mask = new Uint8Array(total);
    const position = this.geometry.getAttribute('position');
    const index = this.geometry.getIndex();
    const triCount = (index ? index.count : position.count) / 3;
    const a = new Vector3();
    const b = new Vector3();
    const c = new Vector3();
    const p = new Vector3();
    const vertexOf = (t: number, k: number) => (index ? index.getX(t * 3 + k) : t * 3 + k);
    const clampIdx = (v: number, count: number) => (v < 0 ? 0 : v >= count ? count - 1 : v);
    const stamp = (q: Vector3) => {
      const ix = clampIdx(Math.floor((q.x - origin.x) / cellSize), counts.x);
      const iy = clampIdx(Math.floor((q.y - origin.y) / cellSize), counts.y);
      const iz = clampIdx(Math.floor((q.z - origin.z) / cellSize), counts.z);
      mask[ix * strides.x + iy * strides.y + iz] = 1;
    };

    const step = cellSize * 0.45;
    for (let t = 0; t < triCount; t++) {
      a.fromBufferAttribute(position, vertexOf(t, 0));
      b.fromBufferAttribute(position, vertexOf(t, 1));
      c.fromBufferAttribute(position, vertexOf(t, 2));
      const maxEdge = Math.max(a.distanceTo(b), b.distanceTo(c), c.distanceTo(a));
      const n = Math.min(512, Math.max(1, Math.ceil(maxEdge / step)));
      for (let i = 0; i <= n; i++) {
        for (let j = 0; j <= n - i; j++) {
          const u = i / n;
          const v = j / n;
          const w = 1 - u - v;
          p.set(
            a.x * w + b.x * u + c.x * v,
            a.y * w + b.y * u + c.y * v,
            a.z * w + b.z * u + c.z * v,
          );
          stamp(p);
        }
      }
    }
    return mask;
  }

  /** Run the 3 orthogonal-axis ray sweeps, accumulating inside-votes per voxel. */
  private computeInsideCounts(grid: VoxelGrid): {
    insideCount: Uint8Array;
    axisReports: AxisSweepDiagnostics[];
  } {
    const insideCount = new Uint8Array(grid.info.total);
    const axisReports = [
      this.sweepAxis(grid, 0, insideCount),
      this.sweepAxis(grid, 1, insideCount),
      this.sweepAxis(grid, 2, insideCount),
    ];
    return { insideCount, axisReports };
  }

  /**
   * Near-surface mask: 6-connected sign-change boundary (plus tangent-ambiguous 1-vote
   * voxels) dilated by ceil(maxBandMm/cell)+1 Chebyshev (26-conn) steps. A voxel at euclidean
   * distance d from the surface has a sign-change within ceil(d/cell) Chebyshev steps, so this
   * dilation provably covers every voxel within `maxBandMm` of the surface. Returns the mask,
   * the linear indices of masked voxels, and the dilation radius used.
   */
  private buildNearSurfaceMask(
    grid: VoxelGrid,
    insideCount: Uint8Array,
    maxBandMm: number,
  ): { mask: Uint8Array; maskedIndices: Int32Array; bandCells: number } {
    const { counts, cellSize, total } = grid.info;
    const mask = new Uint8Array(total);
    let frontier: number[] = [];

    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        for (let z = 0; z < counts.z; z++) {
          const idx = grid.index(x, y, z);
          const ins = insideCount[idx] >= 2;
          let seed = insideCount[idx] === 1;
          if (!seed) {
            for (const [dx, dy, dz] of NEIGHBOR_OFFSETS_6) {
              const nx = x + dx, ny = y + dy, nz = z + dz;
              if (nx < 0 || ny < 0 || nz < 0 || nx >= counts.x || ny >= counts.y || nz >= counts.z) {
                continue;
              }
              if ((insideCount[grid.index(nx, ny, nz)] >= 2) !== ins) {
                seed = true;
                break;
              }
            }
          }
          if (seed) {
            mask[idx] = 1;
            frontier.push(idx);
          }
        }
      }
    }

    const bandCells = Math.ceil(maxBandMm / cellSize) + 1;
    for (let step = 0; step < bandCells && frontier.length > 0; step++) {
      const next: number[] = [];
      for (const idx of frontier) {
        const { x, y, z } = grid.coords(idx);
        for (const [dx, dy, dz] of NEIGHBOR_OFFSETS_26) {
          const nx = x + dx, ny = y + dy, nz = z + dz;
          if (nx < 0 || ny < 0 || nz < 0 || nx >= counts.x || ny >= counts.y || nz >= counts.z) {
            continue;
          }
          const nIdx = grid.index(nx, ny, nz);
          if (mask[nIdx] === 0) {
            mask[nIdx] = 1;
            next.push(nIdx);
          }
        }
      }
      frontier = next;
    }

    let count = 0;
    for (let i = 0; i < total; i++) if (mask[i] === 1) count++;
    const maskedIndices = new Int32Array(count);
    let j = 0;
    for (let i = 0; i < total; i++) if (mask[i] === 1) maskedIndices[j++] = i;

    return { mask, maskedIndices, bandCells };
  }

  /** Assemble the field by querying each masked voxel's closest point inline (main thread). */
  private assembleNarrowBandSync(
    grid: VoxelGrid,
    insideCount: Uint8Array,
    mask: Uint8Array,
    maskedIndices: Int32Array,
    maxBandMm: number,
  ): void {
    const { origin, cellSize } = grid.info;
    const point = new Vector3();
    const closest = new Vector3();
    const hitInfo = { point: closest, distance: 0, faceIndex: 0 };
    const faceNormal = new Vector3();
    const toFace = new Vector3();

    for (let k = 0; k < maskedIndices.length; k++) {
      const idx = maskedIndices[k];
      const { x, y, z } = grid.coords(idx);
      point.set(origin.x + x * cellSize, origin.y + y * cellSize, origin.z + z * cellSize);
      this.bvh.closestPointToPoint(point, hitInfo as Parameters<MeshBVH['closestPointToPoint']>[1]);
      const dist = hitInfo.distance;
      const votes = insideCount[idx];
      let isInside: boolean;
      if (votes >= 2) {
        isInside = true;
      } else if (votes === 1 && dist < cellSize && hitInfo.faceIndex !== undefined && hitInfo.faceIndex >= 0) {
        this.computeFaceNormal(hitInfo.faceIndex, faceNormal);
        toFace.copy(hitInfo.point).sub(point);
        isInside = toFace.dot(faceNormal) > 0;
      } else {
        isInside = false;
      }
      grid.data[idx] = isInside ? -dist : dist;
    }

    this.fillNarrowBandSentinels(grid, insideCount, mask, maxBandMm);
  }

  /**
   * Assemble the field from worker-computed distances (one per masked voxel, in
   * `maskedIndices` order). The rare tangent-ambiguous voxels (1 vote, within a cell of the
   * surface) are resolved on the main thread with a fresh face-normal query.
   */
  private assembleNarrowBandFromDistances(
    grid: VoxelGrid,
    insideCount: Uint8Array,
    mask: Uint8Array,
    maskedIndices: Int32Array,
    maxBandMm: number,
    distances: Float32Array,
  ): void {
    const { cellSize } = grid.info;
    for (let k = 0; k < maskedIndices.length; k++) {
      const idx = maskedIndices[k];
      const dist = distances[k];
      const votes = insideCount[idx];
      let isInside: boolean;
      if (votes >= 2) {
        isInside = true;
      } else if (votes === 1 && dist < cellSize) {
        isInside = this.resolveInsideByNormal(grid, idx);
      } else {
        isInside = false;
      }
      grid.data[idx] = isInside ? -dist : dist;
    }
    this.fillNarrowBandSentinels(grid, insideCount, mask, maxBandMm);
  }

  /** Non-masked voxels are >maxBandMm from the surface: clamp to ±maxBandMm (sign only). */
  private fillNarrowBandSentinels(
    grid: VoxelGrid,
    insideCount: Uint8Array,
    mask: Uint8Array,
    maxBandMm: number,
  ): void {
    const total = grid.info.total;
    for (let i = 0; i < total; i++) {
      if (mask[i] === 0) grid.data[i] = insideCount[i] >= 2 ? -maxBandMm : maxBandMm;
    }
  }

  /** Is a single voxel inside, per the closest face's outward normal (winding-number proxy)? */
  private resolveInsideByNormal(grid: VoxelGrid, idx: number): boolean {
    const { origin, cellSize } = grid.info;
    const { x, y, z } = grid.coords(idx);
    const probe = new Vector3(origin.x + x * cellSize, origin.y + y * cellSize, origin.z + z * cellSize);
    const hitInfo = { point: new Vector3(), distance: 0, faceIndex: 0 };
    this.bvh.closestPointToPoint(probe, hitInfo as Parameters<MeshBVH['closestPointToPoint']>[1]);
    if (hitInfo.faceIndex === undefined || hitInfo.faceIndex < 0) return false;
    const faceNormal = new Vector3();
    this.computeFaceNormal(hitInfo.faceIndex, faceNormal);
    return hitInfo.point.sub(probe).dot(faceNormal) > 0;
  }

  private logNarrowBandDiagnostics(
    grid: VoxelGrid,
    insideCount: Uint8Array,
    axisReports: AxisSweepDiagnostics[],
    maxBandMm: number,
    bandCells: number,
    maskedVoxels: number,
  ): void {
    const { counts, cellSize, total } = grid.info;
    const voteHistogram = [0, 0, 0, 0];
    for (let i = 0; i < total; i++) voteHistogram[insideCount[i]]++;
    const oddRayRateByAxis = axisReports.map((r) => ({
      axis: r.axis,
      oddPct: r.totalRays > 0 ? Number(((100 * r.oddParityRays) / r.totalRays).toFixed(2)) : 0,
    }));
    const suspicious = oddRayRateByAxis.some((r) => r.oddPct > 5);
    const logger = suspicious ? console.warn : console.info;
    logger('[SDFGenerator][narrow-band-diagnostics]', {
      grid: `${counts.x}x${counts.y}x${counts.z}`,
      cellSize,
      bandMm: Number(maxBandMm.toFixed(3)),
      bandCells,
      maskedVoxels,
      maskedPct: Number(((100 * maskedVoxels) / total).toFixed(1)),
      voteHistogram: {
        v0: voteHistogram[0],
        v1: voteHistogram[1],
        v2: voteHistogram[2],
        v3: voteHistogram[3],
      },
      oddRayRateByAxis,
    });
  }

  private resolveAmbiguousByNormal(grid: VoxelGrid, ambiguousIndices: number[]): void {
    const probe = new Vector3();
    const closest = new Vector3();
    const hitInfo = { point: closest, distance: 0, faceIndex: 0 };
    const faceNormal = new Vector3();
    const toFace = new Vector3();
    const { origin, cellSize } = grid.info;

    for (const idx of ambiguousIndices) {
      const { x, y, z } = grid.coords(idx);
      probe.set(
        origin.x + x * cellSize,
        origin.y + y * cellSize,
        origin.z + z * cellSize,
      );
      this.bvh.closestPointToPoint(probe, hitInfo as Parameters<MeshBVH['closestPointToPoint']>[1]);
      if (hitInfo.faceIndex === undefined || hitInfo.faceIndex < 0) continue;
      this.computeFaceNormal(hitInfo.faceIndex, faceNormal);
      toFace.copy(hitInfo.point).sub(probe);
      // Voxel is "inside" if the closest face's outward normal points away from
      // the voxel — i.e. toFace · normal > 0 (the hit lies further along normal
      // than the voxel does).
      if (toFace.dot(faceNormal) > 0) {
        grid.data[idx] = -grid.data[idx];
      }
    }
  }

  /**
   * Inside/outside via top-down column rays (insertion axis ≈ +Z in aligned space).
   */
  private applyInsertionColumnSigns(grid: VoxelGrid): void {
    const { counts, origin, cellSize, bounds } = grid.info;
    const raycaster = new Raycaster();
    const mesh = new Mesh(this.geometry, new MeshBasicMaterial());
    mesh.updateMatrixWorld(true);

    const direction = new Vector3(0, 0, -1);
    const originRay = new Vector3();
    const maxZ = bounds.max.z + cellSize * 2;
    const tolerance = cellSize * 0.25;

    for (let x = 0; x < counts.x; x++) {
      const worldX = origin.x + x * cellSize;
      for (let y = 0; y < counts.y; y++) {
        const worldY = origin.y + y * cellSize;
        originRay.set(worldX, worldY, maxZ);
        raycaster.set(originRay, direction);
        const hits = raycaster.intersectObject(mesh, false);

        let inside = false;
        let hitIndex = 0;
        let nextHitZ = hits[hitIndex]?.point.z ?? -Infinity;

        for (let z = counts.z - 1; z >= 0; z--) {
          const worldZ = origin.z + z * cellSize;
          while (hitIndex < hits.length && worldZ <= nextHitZ + tolerance) {
            inside = !inside;
            hitIndex++;
            nextHitZ = hits[hitIndex]?.point.z ?? -Infinity;
          }

          if (inside) {
            const idx = grid.index(x, y, z);
            grid.data[idx] = -grid.data[idx];
          }
        }
      }
    }

    (mesh.material as MeshBasicMaterial).dispose();
  }

  /**
   * Sweep all rays parallel to a single axis (0 = X, 1 = Y, 2 = Z) and increment
   * `insideCount[voxel]` for every voxel that lies inside the surface along that
   * sweep. Hits within `cellSize * 0.25` along the ray are collapsed.
   */
  private sweepAxis(grid: VoxelGrid, axis: AxisIndex, insideCount: Uint8Array): AxisSweepDiagnostics {
    const layout = describeAxisSweep(grid, axis);
    const dedupTol = grid.info.cellSize * 0.25;
    const ray = new Ray();
    ray.direction.set(axis === 0 ? 1 : 0, axis === 1 ? 1 : 0, axis === 2 ? 1 : 0);
    let axisLabel: 'x' | 'y' | 'z' = 'z';
    if (axis === 0) axisLabel = 'x';
    else if (axis === 1) axisLabel = 'y';
    const report: AxisSweepDiagnostics = {
      axis: axisLabel,
      totalRays: 0,
      oddParityRays: 0,
      trailingInsideCellsOnOdd: 0,
      worstOddRays: [],
    };

    for (let i = 0; i < layout.crossLen1; i++) {
      const wi = layout.crossStart1 + i * layout.cellSize;
      for (let j = 0; j < layout.crossLen2; j++) {
        report.totalRays++;
        const wj = layout.crossStart2 + j * layout.cellSize;
        setRayOriginForAxis(ray, axis, layout.rayStart, wi, wj);

        const hits = this.bvh.raycast(ray, DoubleSide) as Array<{ distance: number }>;
        if (hits.length === 0) continue;
        const sorted = hits.map((h) => h.distance).sort((a, b) => a - b);
        const deduped = this.dedupHitDistances(sorted, dedupTol);
        if ((deduped.length & 1) === 1) {
          report.oddParityRays++;
          const lastHitDistance = deduped[deduped.length - 1];
          const approxLastHitCell = Math.max(
            0,
            Math.floor((lastHitDistance - (layout.sweepOrigin - layout.rayStart)) / layout.cellSize),
          );
          const trailingInsideCells = Math.max(0, layout.sweepLen - approxLastHitCell - 1);
          report.trailingInsideCellsOnOdd += trailingInsideCells;
          this.pushWorstOddRay(report, {
            axis: axisLabel,
            i,
            j,
            hitCount: deduped.length,
            trailingInsideCells,
            tailStartDistance: Number(lastHitDistance.toFixed(3)),
            tailStartCell: approxLastHitCell,
          });
        }

        this.toggleVoxelsAlongAxis(grid, axis, layout, i, j, deduped, insideCount);
      }
    }
    return report;
  }

  private pushWorstOddRay(report: AxisSweepDiagnostics, candidate: WorstOddRay): void {
    if (candidate.trailingInsideCells <= 0) return;
    report.worstOddRays.push(candidate);
    report.worstOddRays.sort((a, b) => b.trailingInsideCells - a.trailingInsideCells);
    if (report.worstOddRays.length > WORST_RAY_LIMIT) {
      report.worstOddRays.length = WORST_RAY_LIMIT;
    }
  }

  private toggleVoxelsAlongAxis(
    grid: VoxelGrid,
    axis: AxisIndex,
    layout: AxisSweepLayout,
    i: number,
    j: number,
    deduped: number[],
    insideCount: Uint8Array,
  ): void {
    // Robust handling: only treat *paired* intersections as inside intervals:
    // [hit0..hit1], [hit2..hit3], ... . If we have an odd number of hits,
    // the last hit is ignored (prevents "inside tails" to grid boundary).
    const pairCount = Math.floor(deduped.length / 2);
    if (pairCount === 0) return;
    let pairIdx = 0;
    let enter = deduped[0];
    let exit = deduped[1];

    for (let k = 0; k < layout.sweepLen; k++) {
      const voxelDistance = k * layout.cellSize + (layout.sweepOrigin - layout.rayStart);
      while (pairIdx < pairCount && voxelDistance > exit + 1e-6) {
        pairIdx++;
        if (pairIdx >= pairCount) break;
        enter = deduped[pairIdx * 2 + 0];
        exit = deduped[pairIdx * 2 + 1];
      }
      if (pairIdx >= pairCount) break;
      if (voxelDistance < enter - 1e-6 || voxelDistance > exit + 1e-6) continue;

      const [gx, gy, gz] = voxelCoordsForAxis(axis, k, i, j);
      insideCount[grid.index(gx, gy, gz)]++;
    }
  }

  private dedupHitDistances(sorted: number[], tolerance: number): number[] {
    if (sorted.length <= 1) return sorted;
    const result: number[] = [sorted[0]];
    let last = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
      const d = sorted[i];
      if (d - last > tolerance) {
        result.push(d);
        last = d;
      }
    }
    return result;
  }

  private computeFaceNormal(faceIndex: number, out: Vector3): void {
    const indexAttr = this.geometry.getIndex();
    const posAttr = this.geometry.getAttribute('position');
    if (!indexAttr || !posAttr) {
      out.set(0, 0, 1);
      return;
    }
    const base = faceIndex * 3;
    const a = indexAttr.getX(base + 0);
    const b = indexAttr.getX(base + 1);
    const c = indexAttr.getX(base + 2);

    const ax = posAttr.getX(a);
    const ay = posAttr.getY(a);
    const az = posAttr.getZ(a);
    const bx = posAttr.getX(b);
    const by = posAttr.getY(b);
    const bz = posAttr.getZ(b);
    const cx = posAttr.getX(c);
    const cy = posAttr.getY(c);
    const cz = posAttr.getZ(c);

    const ux = bx - ax;
    const uy = by - ay;
    const uz = bz - az;
    const vx = cx - ax;
    const vy = cy - ay;
    const vz = cz - az;

    out.set(
      uy * vz - uz * vy,
      uz * vx - ux * vz,
      ux * vy - uy * vx,
    );
    const len = out.length();
    if (len > 1e-12) out.multiplyScalar(1 / len);
  }

  /**
   * Merge multiple meshes into a single geometry with BVH
   */
  private mergeGeometries(meshes: Mesh[]): BufferGeometry {
    const geometries: BufferGeometry[] = [];

    for (const mesh of meshes) {
      if (!mesh?.geometry) continue;
      mesh.updateMatrixWorld(true);
      const geom = (mesh.geometry as BufferGeometry).clone();
      geom.applyMatrix4(mesh.matrixWorld);
      if (!geom.getAttribute('normal')) {
        geom.computeVertexNormals();
      }
      geometries.push(geom);
    }

    if (geometries.length === 0) {
      throw new Error('No valid geometries to process');
    }

    // Count total vertices
    let totalVertices = 0;
    for (const geom of geometries) {
      const nonIndexed = geom.getIndex() ? geom.toNonIndexed() : geom;
      totalVertices += nonIndexed.getAttribute('position').count;
      if (nonIndexed !== geom) nonIndexed.dispose();
    }

    const positions = new Float32Array(totalVertices * 3);
    const normals = new Float32Array(totalVertices * 3);
    const indices = new Uint32Array(totalVertices);

    let offset = 0;
    this.partRanges = [];
    for (const geom of geometries) {
      const nonIndexed = geom.getIndex() ? geom.toNonIndexed() : geom;
      const pos = nonIndexed.getAttribute('position');
      let norm = nonIndexed.getAttribute('normal');
      if (!norm) {
        nonIndexed.computeVertexNormals();
        norm = nonIndexed.getAttribute('normal');
      }

      positions.set(pos.array as ArrayLike<number>, offset * 3);
      normals.set(norm.array as ArrayLike<number>, offset * 3);

      for (let i = 0; i < pos.count; i++) {
        indices[offset + i] = offset + i;
      }

      this.partRanges.push({ start: offset, count: pos.count });
      offset += pos.count;
      if (nonIndexed !== geom) nonIndexed.dispose();
    }

    geometries.forEach((g) => g.dispose());

    const merged = new BufferGeometry();
    merged.setAttribute('position', new Float32BufferAttribute(positions, 3));
    merged.setAttribute('normal', new Float32BufferAttribute(normals, 3));
    merged.setIndex(new Uint32BufferAttribute(indices, 1));

    merged.computeBoundingBox();
    merged.computeBoundingSphere();
    (merged as any).boundsTree = new MeshBVH(merged);

    return merged;
  }

  /**
   * Dispose of internal geometry
   */
  dispose(): void {
    this.signedParts = null;
    this.geometry.dispose();
  }
}

interface AxisSweepLayout {
  sweepLen: number;
  crossLen1: number;
  crossLen2: number;
  rayStart: number;
  sweepOrigin: number;
  crossStart1: number;
  crossStart2: number;
  cellSize: number;
}

interface AxisSweepDiagnostics {
  axis: 'x' | 'y' | 'z';
  totalRays: number;
  oddParityRays: number;
  trailingInsideCellsOnOdd: number;
  worstOddRays: WorstOddRay[];
}

interface WorstOddRay {
  axis: 'x' | 'y' | 'z';
  i: number;
  j: number;
  hitCount: number;
  trailingInsideCells: number;
  tailStartDistance: number;
  tailStartCell: number;
}

function describeAxisSweep(grid: VoxelGrid, axis: AxisIndex): AxisSweepLayout {
  const { counts, origin, cellSize, bounds } = grid.info;
  if (axis === 0) {
    return {
      sweepLen: counts.x,
      crossLen1: counts.y,
      crossLen2: counts.z,
      rayStart: bounds.min.x - cellSize * 2,
      sweepOrigin: origin.x,
      crossStart1: origin.y,
      crossStart2: origin.z,
      cellSize,
    };
  }
  if (axis === 1) {
    return {
      sweepLen: counts.y,
      crossLen1: counts.x,
      crossLen2: counts.z,
      rayStart: bounds.min.y - cellSize * 2,
      sweepOrigin: origin.y,
      crossStart1: origin.x,
      crossStart2: origin.z,
      cellSize,
    };
  }
  return {
    sweepLen: counts.z,
    crossLen1: counts.x,
    crossLen2: counts.y,
    rayStart: bounds.min.z - cellSize * 2,
    sweepOrigin: origin.z,
    crossStart1: origin.x,
    crossStart2: origin.y,
    cellSize,
  };
}

function setRayOriginForAxis(ray: Ray, axis: AxisIndex, rayStart: number, wi: number, wj: number): void {
  if (axis === 0) ray.origin.set(rayStart, wi, wj);
  else if (axis === 1) ray.origin.set(wi, rayStart, wj);
  else ray.origin.set(wi, wj, rayStart);
}

function voxelCoordsForAxis(axis: AxisIndex, k: number, i: number, j: number): [number, number, number] {
  if (axis === 0) return [k, i, j];
  if (axis === 1) return [i, k, j];
  return [i, j, k];
}


/** Max cells the sealing closure may bridge across to plug a leak. */
const MAX_SEAL_RADIUS = 4;
/** Enclosed volume below this share of the mesh's own volume means it drained. */
const DRAIN_THRESHOLD = 0.5;

/**
 * Leak-tolerant "outside" classification.
 *
 * A plain flood is all-or-nothing: ONE unstamped voxel anywhere on the surface
 * — a seam slit between a tooth and the gum, a crack the rasterizer sampled
 * past — lets the exterior pour into the model and the whole interior comes
 * back "outside", which downstream collapses the shell to a one-voxel film.
 * Scan unions are never watertight, so the barrier is morphologically sealed
 * instead: dilate it by r, flood, then grow the exterior back r steps (never
 * into the original boundary) so the classification stays accurate everywhere
 * the seal was not load-bearing.
 *
 * r is measured, not guessed, and the metric has to distinguish two things a
 * bigger seal both cause: plugging a leak (what we want) and filling narrow
 * exterior crevices such as interdental gaps (what we must not do). They are
 * told apart by DEPTH — a seal only ever reaches ~2r cells past the surface, so
 * crevice fill is confined to the narrow band, while plugging a leak hands back
 * interior volume far beyond it. Escalation therefore tracks enclosed voxels
 * OUTSIDE the band and stops as soon as growing the seal stops recovering any.
 */
function floodOutsideSealed(
  grid: VoxelGrid,
  boundary: Uint8Array,
  referenceVolume: number,
): {
  outside: Uint8Array;
  sealRadius: number;
  insideVoxels: number;
  volumeCurve: number[];
} {
  const total = grid.info.total;
  const cellVolume = grid.info.cellSize ** 3;
  const volumeCurve: number[] = [];

  let outside = floodOutsideMask(grid, boundary);
  let radius = 0;
  for (;;) {
    const enclosed = (total - countMask(outside)) * cellVolume;
    volumeCurve.push(Math.round(enclosed));
    // Enough of the mesh's own volume came back → the barrier held.
    if (enclosed >= referenceVolume * DRAIN_THRESHOLD || radius >= MAX_SEAL_RADIUS) break;
    radius++;
    outside = floodOutsideMask(grid, closeMask(grid, boundary, radius));
  }
  return {
    outside,
    sealRadius: radius,
    insideVoxels: total - countMask(outside),
    volumeCurve,
  };
}

function countMask(mask: Uint8Array): number {
  let n = 0;
  for (let i = 0; i < mask.length; i++) n += mask[i];
  return n;
}

/**
 * Morphological closing: bridges gaps narrower than 2·radius and leaves the
 * barrier untouched everywhere else. Preferred over a plain dilation because a
 * dilated barrier thickens the whole surface — which shifts the flood boundary
 * and would have to be grown back — whereas a closure only adds material where
 * a gap needed spanning. Passages narrower than 2·radius do get filled, but as
 * BARRIER, not as interior: their sign is settled afterwards by the Lipschitz
 * pass from the real distances, so a tight interdental gap stays open.
 */
function closeMask(grid: VoxelGrid, mask: Uint8Array, radius: number): Uint8Array {
  const dilated = dilateMaskChebyshev(grid, mask, radius);
  const closed = erodeMaskChebyshev(grid, dilated, radius);
  for (let i = 0; i < closed.length; i++) if (mask[i]) closed[i] = 1;
  return closed;
}

/** Chebyshev erosion — dilation of the complement, out-of-grid counted as set. */
function erodeMaskChebyshev(grid: VoxelGrid, mask: Uint8Array, radius: number): Uint8Array {
  const complement = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i++) complement[i] = mask[i] ? 0 : 1;
  const grown = dilateMaskChebyshev(grid, complement, radius);
  const out = new Uint8Array(mask.length);
  for (let i = 0; i < mask.length; i++) out[i] = grown[i] ? 0 : 1;
  return out;
}

/**
 * Chebyshev (26-connected) dilation by `radius`, run as three 1-D passes. A
 * 26-connected barrier is what a 6-connected flood cannot slip through
 * diagonally, so this is the right neighbourhood for sealing.
 */
function dilateMaskChebyshev(grid: VoxelGrid, mask: Uint8Array, radius: number): Uint8Array {
  const { counts, strides, total } = grid.info;
  let src = mask;
  const axes: Array<{ stride: number; length: number }> = [
    { stride: strides.x, length: counts.x },
    { stride: strides.y, length: counts.y },
    { stride: 1, length: counts.z },
  ];
  for (const { stride, length } of axes) {
    const dst = new Uint8Array(total);
    const lineCount = total / length;
    for (let line = 0; line < lineCount; line++) {
      // Decompose the line's base offset from its index in the other two axes.
      const base = lineBaseOffset(line, stride, length, strides, counts);
      let last = -Infinity;
      for (let i = 0; i < length; i++) {
        const idx = base + i * stride;
        if (src[idx]) last = i;
        if (i - last <= radius) dst[idx] = 1;
      }
      last = Infinity;
      for (let i = length - 1; i >= 0; i--) {
        const idx = base + i * stride;
        if (src[idx]) last = i;
        if (last - i <= radius) dst[idx] = 1;
      }
    }
    src = dst;
  }
  return src;
}

/** Base linear offset of the `line`-th 1-D run along the axis with `stride`. */
function lineBaseOffset(
  line: number,
  stride: number,
  length: number,
  strides: { x: number; y: number },
  counts: { x: number; y: number; z: number },
): number {
  if (stride === strides.x) {
    // Lines along x are indexed by (y, z).
    const y = Math.floor(line / counts.z);
    return y * strides.y + (line - y * counts.z);
  }
  if (stride === strides.y) {
    // Lines along y are indexed by (x, z).
    const x = Math.floor(line / counts.z);
    return x * strides.x + (line - x * counts.z);
  }
  // Lines along z are indexed by (x, y); each occupies `length` contiguous cells.
  return line * length;
}

/**
 * Flood-fill "outside" classification via alternating raster sweeps: a voxel is
 * outside when it is not a boundary voxel and touches the grid face or an
 * already-outside 6-neighbour. Sweeps repeat until stable (the exterior of a
 * jaw-like shape converges in a few passes); every unreached voxel is inside.
 */
function floodOutsideMask(grid: VoxelGrid, boundary: Uint8Array): Uint8Array {
  const { counts, strides, total } = grid.info;
  const outside = new Uint8Array(total);
  const sx = strides.x;
  const sy = strides.y;

  const maxSweeps = 24;
  for (let sweep = 0; sweep < maxSweeps; sweep++) {
    let changed = false;
    // Forward pass: look at -x / -y / -z neighbours (grid face counts as outside).
    for (let x = 0; x < counts.x; x++) {
      for (let y = 0; y < counts.y; y++) {
        let idx = x * sx + y * sy;
        for (let z = 0; z < counts.z; z++, idx++) {
          if (outside[idx] || boundary[idx]) continue;
          if (
            (x === 0 || outside[idx - sx]) ||
            (y === 0 || outside[idx - sy]) ||
            (z === 0 || outside[idx - 1])
          ) {
            outside[idx] = 1;
            changed = true;
          }
        }
      }
    }
    // Backward pass: look at +x / +y / +z neighbours.
    for (let x = counts.x - 1; x >= 0; x--) {
      for (let y = counts.y - 1; y >= 0; y--) {
        let idx = x * sx + y * sy + counts.z - 1;
        for (let z = counts.z - 1; z >= 0; z--, idx--) {
          if (outside[idx] || boundary[idx]) continue;
          if (
            (x === counts.x - 1 || outside[idx + sx]) ||
            (y === counts.y - 1 || outside[idx + sy]) ||
            (z === counts.z - 1 || outside[idx + 1])
          ) {
            outside[idx] = 1;
            changed = true;
          }
        }
      }
    }
    if (!changed) break;
  }
  return outside;
}

/**
 * Linear indices of every voxel within `radiusCells` 6-connected BFS steps of a
 * boundary voxel — the narrow band that gets exact closest-point distances.
 */
function collectBandIndices(grid: VoxelGrid, boundary: Uint8Array, radiusCells: number): Int32Array {
  const { counts, strides, total } = grid.info;
  const sx = strides.x;
  const sy = strides.y;
  const visited = new Uint8Array(total);
  const out: number[] = [];
  let frontier: number[] = [];
  for (let i = 0; i < total; i++) {
    if (boundary[i]) {
      visited[i] = 1;
      frontier.push(i);
      out.push(i);
    }
  }
  for (let depth = 0; depth < radiusCells; depth++) {
    const next: number[] = [];
    for (const idx of frontier) {
      const x = Math.floor(idx / sx);
      const rem = idx - x * sx;
      const y = Math.floor(rem / sy);
      const z = rem - y * sy;
      if (x > 0 && !visited[idx - sx]) { visited[idx - sx] = 1; next.push(idx - sx); out.push(idx - sx); }
      if (x < counts.x - 1 && !visited[idx + sx]) { visited[idx + sx] = 1; next.push(idx + sx); out.push(idx + sx); }
      if (y > 0 && !visited[idx - sy]) { visited[idx - sy] = 1; next.push(idx - sy); out.push(idx - sy); }
      if (y < counts.y - 1 && !visited[idx + sy]) { visited[idx + sy] = 1; next.push(idx + sy); out.push(idx + sy); }
      if (z > 0 && !visited[idx - 1]) { visited[idx - 1] = 1; next.push(idx - 1); out.push(idx - 1); }
      if (z < counts.z - 1 && !visited[idx + 1]) { visited[idx + 1] = 1; next.push(idx + 1); out.push(idx + 1); }
    }
    frontier = next;
  }
  return Int32Array.from(out);
}

/**
 * Flips "outside"-signed band cells that cannot reach the exterior. The union
 * signed distance is local geometry — inside a fully enclosed void it happily
 * reports "outside the union", while the far field (flood) calls the void
 * inside; the sign conflict at the band edge would extrude a phantom surface.
 * BFS from positive band cells that touch the beyond-band exterior, walking
 * 6-connected positive band cells; whatever positive cell stays unreached is
 * enclosed and flips inside. Genuine narrow slots survive — their positive
 * chain reaches open air at the slot mouth.
 */
function reconnectOutsidePositive(
  grid: VoxelGrid,
  inBand: Uint8Array,
  outside: Uint8Array,
  maskedIndices: Int32Array,
): void {
  const { counts, strides } = grid.info;
  const sx = strides.x;
  const sy = strides.y;
  const data = grid.data;
  const reached = new Uint8Array(grid.info.total);

  const neighbours = (idx: number, visit: (n: number) => void) => {
    const x = Math.floor(idx / sx);
    const rem = idx - x * sx;
    const y = Math.floor(rem / sy);
    const z = rem - y * sy;
    if (x > 0) visit(idx - sx);
    if (x < counts.x - 1) visit(idx + sx);
    if (y > 0) visit(idx - sy);
    if (y < counts.y - 1) visit(idx + sy);
    if (z > 0) visit(idx - 1);
    if (z < counts.z - 1) visit(idx + 1);
  };

  let frontier: number[] = [];
  for (let k = 0; k < maskedIndices.length; k++) {
    const idx = maskedIndices[k];
    if (data[idx] <= 0 || reached[idx]) continue;
    let touchesExterior = false;
    neighbours(idx, (n) => {
      if (!inBand[n] && outside[n]) touchesExterior = true;
    });
    if (touchesExterior) {
      reached[idx] = 1;
      frontier.push(idx);
    }
  }
  while (frontier.length > 0) {
    const next: number[] = [];
    for (const idx of frontier) {
      neighbours(idx, (n) => {
        if (reached[n] || !inBand[n] || data[n] <= 0) return;
        reached[n] = 1;
        next.push(n);
      });
    }
    frontier = next;
  }
  for (let k = 0; k < maskedIndices.length; k++) {
    const idx = maskedIndices[k];
    if (data[idx] > 0 && !reached[idx]) data[idx] = -data[idx];
  }
}

/**
 * Resolves the sign of still-ambiguous voxels by Lipschitz consistency.
 * A true SDF changes by at most one cell between 6-neighbours; each flagged
 * voxel therefore takes the sign (±its unsigned distance) that violates that
 * bound least against its already-signed neighbours. Gauss–Seidel passes let
 * multi-layer crusts settle from the outside in; anything left unresolved
 * keeps its flood-assigned value.
 */
function resolveBoundarySignsByLipschitz(
  grid: VoxelGrid,
  boundary: Uint8Array,
  maskedIndices: Int32Array,
  distances: Float32Array,
  band: number,
): void {
  const { counts, strides, cellSize } = grid.info;
  const sx = strides.x;
  const sy = strides.y;
  const data = grid.data;
  const margin = cellSize * 1.05;

  for (let pass = 0; pass < 4; pass++) {
    let changed = 0;
    for (let k = 0; k < maskedIndices.length; k++) {
      const idx = maskedIndices[k];
      if (!boundary[idx]) continue;
      const d = Math.min(distances[k], band);
      const x = Math.floor(idx / sx);
      const rem = idx - x * sx;
      const y = Math.floor(rem / sy);
      const z = rem - y * sy;

      let violPos = 0;
      let violNeg = 0;
      let refs = 0;
      const consider = (n: number) => {
        if (boundary[n]) return;
        const v = data[n];
        violPos += Math.max(0, Math.abs(d - v) - margin);
        violNeg += Math.max(0, Math.abs(-d - v) - margin);
        refs++;
      };
      if (x > 0) consider(idx - sx);
      if (x < counts.x - 1) consider(idx + sx);
      if (y > 0) consider(idx - sy);
      if (y < counts.y - 1) consider(idx + sy);
      if (z > 0) consider(idx - 1);
      if (z < counts.z - 1) consider(idx + 1);
      if (refs === 0) continue;

      let value: number;
      if (violPos < violNeg) value = d;
      else if (violNeg < violPos) value = -d;
      else value = data[idx] < 0 ? -d : d;
      data[idx] = value;
      boundary[idx] = 0;
      changed++;
    }
    if (changed === 0) break;
  }
}
